import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  InjectionToken,
  OnDestroy,
  Renderer2,
  TemplateRef
} from "@angular/core";
import { Subscription } from "rxjs";

import { OverlayRef, PositionOption } from "@lantern/app/core";
import { Status } from "@lantern/app/models";

export const TOOLTIP_MESSAGE = new InjectionToken<TooltipContent | string>(
  "TOOLTIP_MESSAGE"
);
export const TOOLTIP_STATUS = new InjectionToken<Status | "">("TOOLTIP_STATUS");

export interface TooltipContent {
  heading?: string;
  body?: string;
}

export type TooltipMessage = TooltipContent | string | TemplateRef<void>;

const TOOLTIP_POSITION_CLASSES = {
  top: "tooltip--top",
  bottom: "tooltip--bottom",
  left: "tooltip--left",
  right: "tooltip--right"
};

@Component({
  selector: "ta-tooltip",
  template: `
    <div [ngSwitch]="messageType">
      <ng-template ngSwitchCase="content">
        <p class="tooltip__heading" *ngIf="message.heading">
          {{ message.heading }}
        </p>
        <p *ngIf="message.body" class="tooltip__body">{{ message.body }}</p>
      </ng-template>
      <ng-template ngSwitchCase="html">
        <div [innerHTML]="message"></div>
      </ng-template>
      <ng-template ngSwitchCase="template">
        <ng-template [ngTemplateOutlet]="message"></ng-template>
      </ng-template>
    </div>
  `
})
export class TooltipComponent implements AfterViewInit, OnDestroy {
  message: TooltipMessage;
  messageType: "html" | "content" | "template";
  status: Status | "" = "";

  @HostBinding("class.tooltip")
  hostClass = true;

  constructor(
    private elemRef: ElementRef,
    private renderer: Renderer2,
    private overlayRef: OverlayRef,
    @Inject(TOOLTIP_MESSAGE) message: any,
    @Inject(TOOLTIP_STATUS) status: Status | ""
  ) {
    this.status = status;
    this.message = message;
    if (message instanceof TemplateRef) {
      this.messageType = "template";
    } else if (typeof message === "string") {
      this.messageType = "html";
    } else {
      this.messageType = "content";
    }
  }

  private overlayRefPositionChangeSubscription?: Subscription;
  ngAfterViewInit(): void {
    this.initHostStyles();

    this.overlayRefPositionChangeSubscription = this.overlayRef.currentPositionChange.subscribe(
      (currentPosition) => {
        this.setTooltipPositionClass(currentPosition);
      }
    );
    this.setTooltipPositionClass(
      this.overlayRef.currentPosition != null
        ? this.overlayRef.currentPosition
        : "top"
    );
  }

  ngOnDestroy(): void {
    if (this.overlayRefPositionChangeSubscription != null) {
      this.overlayRefPositionChangeSubscription.unsubscribe();
    }
  }

  private initHostStyles(): void {
    this.renderer.addClass(this.elemRef.nativeElement, "tooltip");
    this.setTooltipStatusClass();
  }

  private setTooltipStatusClass(): void {
    if (this.status === "") {
      return;
    }

    this.renderer.addClass(
      this.elemRef.nativeElement,
      `tooltip--${this.status}`
    );
  }

  private setTooltipPositionClass(currentPosition: PositionOption): void {
    Object.keys(TOOLTIP_POSITION_CLASSES).forEach((key) => {
      this.renderer.addClass(
        this.elemRef.nativeElement,
        TOOLTIP_POSITION_CLASSES[key]
      );
    });
  }
}
